import { BehaviorSubject, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { serverurl } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { SnackService } from './snack.service';
import { ClipboardService } from './clipboard.service';

@Injectable({
    providedIn: 'root'
})
export class AiService {

    showAiChat = new BehaviorSubject<boolean>(false);
    showHintAnimation = new BehaviorSubject<boolean>(false);
    activeHint = new BehaviorSubject<boolean>(false);
    aiChat = new BehaviorSubject<any[]>([]);
    // agentList = new BehaviorSubject<any[]>([]);
    showHintAnimationChat = new BehaviorSubject<any[]>([]);

    modelId = new BehaviorSubject<string>('HOMECHAT');
    agentId = new BehaviorSubject<string>('flying_jhonny');

    threadId: string | undefined;

    contentForInput = new BehaviorSubject<{ 'id': string, "text": string, "context_id": string | undefined } | undefined>(undefined);

    counter: number = 0;

    constructor(
        private http: HttpClient,
        private snack: SnackService,
        private clipboardService: ClipboardService,
    ) { }

    private sanitizeAssistantText(text: string): string {
        if (!text) {
            return '';
        }

        return text
            .replace(/^\s*(user|assistant|system)\s*:\s*/gim, '')
            .replace(/\n\s*(user|assistant|system)\s*:\s*/gim, '\n')
            .trim();
    }


    streamDefaultPost(contentId: string, userText: string, questionHidden: boolean = false): Observable<{
        type: 'meta' | 'token' | 'done' | 'error';
        text: string;
        token?: string;
        meta?: any;
        error?: string;
    }> {
        return new Observable(observer => {
            const controller = new AbortController();

            (async () => {
                try {
                    const resp = await fetch(`${serverurl}/aiservice/stream-default`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'text/event-stream',
                        },
                        credentials: 'include', // wichtig wegen Session-Cookie
                        body: JSON.stringify({ contentId, userText }),
                        signal: controller.signal,
                    });

                    if (!resp.ok || !resp.body) {
                        observer.error(new Error(`Stream request failed (${resp.status})`));
                        return;
                    }

                    const reader = resp.body.getReader();
                    const decoder = new TextDecoder('utf-8');
                    let buffer = '';
                    let aggregated = '';

                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) break;

                        buffer += decoder.decode(value, { stream: true });

                        // SSE-Frames sind durch \n\n getrennt
                        const frames = buffer.split('\n\n');
                        buffer = frames.pop() || '';

                        for (const frame of frames) {
                            const lines = frame.split('\n').filter(Boolean);
                            let eventName = 'message';
                            const dataLines: string[] = [];

                            for (const line of lines) {
                                if (line.startsWith('event:')) {
                                    eventName = line.slice(6).trim();
                                } else if (line.startsWith('data:')) {
                                    dataLines.push(line.slice(5).trim());
                                } else if (line.startsWith(':')) {
                                    // Kommentar/Heartbeat -> ignorieren
                                }
                            }

                            const dataRaw = dataLines.join('\n');

                            if (eventName === 'meta') {
                                let meta: any = dataRaw;
                                try { meta = JSON.parse(dataRaw); } catch { }
                                observer.next({ type: 'meta', meta, text: aggregated });
                                continue;
                            }

                            if (eventName === 'done') {
                                observer.next({ type: 'done', text: aggregated });
                                observer.complete();
                                return;
                            }

                            if (eventName === 'error') {
                                let message = dataRaw;
                                try { message = JSON.parse(dataRaw)?.message ?? dataRaw; } catch { }
                                observer.next({ type: 'error', error: message, text: aggregated });
                                observer.complete();
                                return;
                            }

                            // Standard-"message" (Tokens)
                            if (dataRaw) {
                                try {
                                    const payload = JSON.parse(dataRaw);
                                    if (payload?.token) {
                                        aggregated += payload.token;
                                        observer.next({ type: 'token', token: payload.token, text: aggregated });
                                    }
                                } catch {
                                    // Ungültiges JSON im Datenfeld ignorieren
                                }
                            }
                        }
                    }

                    // Falls Server ohne 'done' beendet hat:
                    observer.next({ type: 'done', text: aggregated });
                    observer.complete();

                } catch (err: any) {
                    // Bei manuellem Abbruch kein Fehler pushen
                    if (!controller.signal.aborted) {
                        observer.error(err);
                    }
                }
            })();

            // Teardown: Stream sauber abbrechen
            return () => controller.abort();
        });
    }


    async aiGeneratedReport(content: string, reportType: string) {
        return new Promise((resolve) => {
            this.http.post(serverurl + '/aiservice/clean-airesponse', { "content": content, "promptId": reportType }).subscribe({
                next: (data: any) => {
                    resolve(data);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }

    async showAiChatHint(chatDialogue: any[]) {
        this.activeHint.next(true);
        this.showHintAnimation.next(true);
        this.showHintAnimationChat.next(chatDialogue);
    }
    async openAiChatWithContext(chatDialogue: any[]) {
        this.showAiChat.next(true);
        this.aiChat.next(chatDialogue);
    }
    async aiMiroExport(pins: any[]): Promise<boolean> {
        return new Promise((resolve) => {
            this.http.post(serverurl + '/aiservice/clean-airesponse', { "content": JSON.stringify(pins), "promptId": "EXPORT_MIRO" }).subscribe({
                next: async (data: any) => {
                    data.promptResult = data.promptResult.substring(data.promptResult.indexOf('|'));

                    await this.clipboardService.copyToClipboard(data.promptResult);

                    resolve(true);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
    async getAgentsList(): Promise<any[]> {
        return new Promise((resolve) => {
            return this.http.get(serverurl + '/aiservice/agents').subscribe({
                next: (data: any) => resolve(data),
                error: (err: any) => {
                    this.snack.error(err);
                    resolve([])
                }
            })

        });
    }
    async feedbackCardConversation(userText: string, cardthreadId: string | undefined) {
        return new Promise((resolve) => {
            this.http.post(serverurl + '/aiservice/default', { "agentId": this.agentId.getValue(), "modelId": this.modelId.getValue(), userText, threadId: cardthreadId }).subscribe({
                next: (data: any) => {
                    if (data.threadId) this.threadId = data.threadId;
                    resolve(data);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
    async defaultAiService(contentId: string, userText: string, questionHidden: boolean = false): Promise<any> {
        this.modelId.next(contentId);
        let chat = this.aiChat.getValue();

        // Zeigt die Eingabe des Nutzers schonmal im Chat an
        let preData = {
            id: new Date().getTime(),
            question: userText || undefined,
        }
        if (questionHidden === true) {
            preData.question = undefined;
        }
        chat.push(preData);
        this.aiChat.next(chat);

        return new Promise((resolve) => {
            this.http.post(serverurl + '/aiservice/default', { "agentId": this.agentId.getValue(), "modelId": this.modelId.getValue(), userText, threadId: this.threadId }).subscribe({
                next: (data: any) => {

                    if (data.threadId) this.threadId = data.threadId;
                    if (typeof data.message === 'string') {
                        try {
                            data.message = JSON.parse(data.message);
                        } catch (error) {
                            data.message = [{ answer: data.message, dialogMessage: [], formTransfer: [] }]
                            console.log('Error parsing message:', error);
                            console.log('Log:', data);
                        }
                    }
                    let chatObj;
                    if (Array.isArray(data.message)) {


                        chatObj = {
                            context_id: data.message[0]?.context_id,
                            answer: this.sanitizeAssistantText(data.message[0]?.answer || ''),
                            dialogMessage: data.message[0]?.dialogMessage,
                            formTransfer: data.message[0]?.formTransfer,
                            upload: data.message[0]?.upload,
                            fill_all: data.message[0]?.fill_all,
                        }

                    } else {
                        chatObj = {
                            context_id: data.message.context_id,
                            answer: this.sanitizeAssistantText(data.message.answer || ''),
                            dialogMessage: data.message.dialogMessage,
                            formTransfer: data.message.formTransfer,
                            upload: data.message.upload,
                            fill_all: data.message.fill_all,
                        }
                    }


                    chat.forEach(entry => {
                        if (entry.id === preData.id) {
                            entry.context_id = chatObj.context_id;
                            entry.answer = chatObj.answer;
                            if (!entry.answer || !entry.answer.trim()) {
                                entry.answer = 'I need a bit more context to help effectively. Please share your goal, current state, and one specific question.';
                            }
                            entry.dialogMessage = chatObj.dialogMessage;
                            entry.formTransfer = chatObj.formTransfer;
                            entry.upload = chatObj.upload;
                            entry.fill_all = chatObj.fill_all;
                        }
                    })
                    this.aiChat.next(chat);
                    if (this.showAiChat.getValue() === false) {
                        this.showAiChat.next(true);
                    }
                    resolve(data);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }

    resetChat() {
        this.showAiChat.next(false);
        this.aiChat.next([]);
        this.threadId = undefined;

        console.log("Chat und Thread wurden zurückgesetzt.");

    }
    async getAiResponse(
        streamId: string,
        stepId: string,
        content: string,
        promptType: string,
        stepPromptNumber: number,
        roundId: string,
    ): Promise<any | false> {
        return new Promise((resolve) => {
            this.http.post(serverurl + '/aiservice/airesponse', { streamId, stepId, content, promptType, stepPromptNumber, roundId }).subscribe({
                next: (data: any) => {
                    resolve(data);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
    async getAiStandaloneResponse(promptId: string, userInput: any, roundId: string | undefined = undefined): Promise<any> {
        if (typeof userInput != "string") {
            userInput = JSON.stringify(userInput);
        }
        return new Promise((resolve) => {
            this.http.post(serverurl + '/aiservice/standalone', { promptId, userInput, roundId }).subscribe({
                next: (data: any) => {
                    resolve(data);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
}

